import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';

import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import type {CartStore} from '../../../stores/cartStore';
import type {SingleUsePaymentToken} from '../../../transport/types/checkout';
import {logger, serializeError} from '../../../utils/logger';
import {getNativeBrowserData, getWeb3DsBrowserData} from '../util/threeDS';

export interface GooglePayPaymentHandlerParams {
  cartStore: CartStore;
  logTag: string;
  source: 'web' | 'android';
}

/**
 * Shared Google Pay payment handler.
 *
 * Unlike Apple Pay, the token is already built by googlePayService inside
 * useGooglePayNative — this handler just posts it to checkoutOrder.
 */
export function createGooglePayPaymentHandler({
  cartStore,
  logTag,
  source,
}: GooglePayPaymentHandlerParams) {
  return async (singleUsePaymentToken: SingleUsePaymentToken): Promise<boolean> => {
    if (!singleUsePaymentToken?.token || !singleUsePaymentToken?.payment_service_id) {
      logger.error(logTag, 'Google Pay payment failed - invalid token');
      Toast.show({
        type: ERROR_MESSAGE,
        text1: 'Payment authorization failed. Please try again.',
      });
      return false;
    }

    logger.info(logTag, `${source} Google Pay payment authorized`, {
      paymentServiceId: singleUsePaymentToken.payment_service_id,
    });

    try {
      const browserData = Platform.OS === 'web' ? getWeb3DsBrowserData() : getNativeBrowserData();

      const succeeded = await cartStore.checkoutOrder(
        undefined, // Navigation handled internally by cartStore via RootNavigation
        browserData,
        undefined,
        singleUsePaymentToken,
      );

      logger.info(logTag, `${source} Google Pay checkout submitted`, {succeeded});
      return succeeded === true;
    } catch (error) {
      logger.error(logTag, `${source} Google Pay payment error`, serializeError(error));
      Toast.show({
        type: ERROR_MESSAGE,
        text1: 'Payment failed. Please try again.',
      });
      return false;
    }
  };
}
